import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { agentApi, type ChannelType } from '@/lib/api';
import { Bot, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import styles from './_auth.agents.new.module.css';

export const Route = createFileRoute('/_auth/agents/new')({
  component: NewAgentPage,
});

const STEPS = ['Basic Info', 'Routing', 'Orchestration', 'Review'];

const CHANNEL_OPTIONS: { value: ChannelType; label: string; emoji: string }[] = [
  { value: 'WEB_CHAT', label: 'Web Chat', emoji: '💬' },
  { value: 'API', label: 'API', emoji: '⚡' },
  { value: 'EMAIL', label: 'Email', emoji: '📧' },
  { value: 'WHATSAPP', label: 'WhatsApp', emoji: '📱' },
];

export default function NewAgentPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: '',
    description: '',
    routingChannels: [] as ChannelType[],
    routingPriority: 100,
    systemPrompt: '',
    maxTurnsBeforeEscalation: 10,
  });

  const update = (key: string, value: unknown) => setForm(f => ({ ...f, [key]: value }));

  const toggleChannel = (ch: ChannelType) => {
    setForm(f => ({
      ...f,
      routingChannels: f.routingChannels.includes(ch)
        ? f.routingChannels.filter(c => c !== ch)
        : [...f.routingChannels, ch],
    }));
  };

  async function handleCreate() {
    setSaving(true);
    setError(null);
    try {
      const agent = await agentApi.create({
        name: form.name,
        description: form.description,
        routingChannels: form.routingChannels,
        routingPriority: form.routingPriority,
      });
      if (form.systemPrompt || form.maxTurnsBeforeEscalation !== 10) {
        await agentApi.upsertOrchestration(agent.id, {
          systemPrompt: form.systemPrompt || undefined,
          maxTurnsBeforeEscalation: form.maxTurnsBeforeEscalation,
        });
      }
      await navigate({ to: '/agents/$agentId', params: { agentId: agent.id } });
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  const canNext = step === 0 ? form.name.trim().length > 0 : true;

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {/* Header */}
        <div className={styles.header}>
          <Bot size={28} className={styles.headerIcon} />
          <div>
            <h1 className={styles.title}>Create Agent</h1>
            <p className={styles.subtitle}>Configure your AI agent step by step</p>
          </div>
        </div>

        {/* Step indicator */}
        <div className={styles.steps}>
          {STEPS.map((label, i) => (
            <div key={label} className={`${styles.step} ${i < step ? styles.done : ''} ${i === step ? styles.active : ''}`}>
              <div className={styles.stepDot}>
                {i < step ? <Check size={12} /> : i + 1}
              </div>
              <span className={styles.stepLabel}>{label}</span>
              {i < STEPS.length - 1 && <div className={styles.stepLine} />}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className={styles.body}>
          {step === 0 && (
            <div className={styles.fields}>
              <div className={styles.field}>
                <label className={styles.label}>Agent Name <span className={styles.required}>*</span></label>
                <input
                  id="agent-name"
                  className={styles.input}
                  placeholder="e.g. Customer Support Agent"
                  value={form.name}
                  onChange={e => update('name', e.target.value)}
                  autoFocus
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Description</label>
                <textarea
                  id="agent-description"
                  className={styles.textarea}
                  placeholder="What does this agent do? (optional)"
                  value={form.description}
                  onChange={e => update('description', e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className={styles.fields}>
              <div className={styles.field}>
                <label className={styles.label}>Channels</label>
                <p className={styles.hint}>Which channels should this agent handle? Leave empty to handle all.</p>
                <div className={styles.channelGrid}>
                  {CHANNEL_OPTIONS.map(ch => (
                    <button
                      key={ch.value}
                      id={`channel-${ch.value}`}
                      type="button"
                      className={`${styles.channelBtn} ${form.routingChannels.includes(ch.value) ? styles.selected : ''}`}
                      onClick={() => toggleChannel(ch.value)}
                    >
                      <span className={styles.channelEmoji}>{ch.emoji}</span>
                      <span>{ch.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Routing Priority</label>
                <p className={styles.hint}>Lower number = tried first. Default: 100.</p>
                <input
                  id="agent-priority"
                  type="number"
                  className={styles.input}
                  value={form.routingPriority}
                  onChange={e => update('routingPriority', Number(e.target.value))}
                  min={1}
                  max={999}
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className={styles.fields}>
              <div className={styles.field}>
                <label className={styles.label}>System Prompt</label>
                <p className={styles.hint}>The core instruction set for this agent. Markdown is supported.</p>
                <textarea
                  id="agent-system-prompt"
                  className={styles.textarea}
                  placeholder={`You are a helpful assistant for [Company Name].\nBe concise, friendly, and professional.\nAlways offer to escalate to a human if you cannot help.`}
                  value={form.systemPrompt}
                  onChange={e => update('systemPrompt', e.target.value)}
                  rows={8}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Max turns before escalation</label>
                <p className={styles.hint}>After this many exchanges, automatically hand off to a human operator.</p>
                <input
                  id="agent-max-turns"
                  type="number"
                  className={styles.input}
                  value={form.maxTurnsBeforeEscalation}
                  onChange={e => update('maxTurnsBeforeEscalation', Number(e.target.value))}
                  min={1}
                  max={100}
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className={styles.review}>
              <h3 className={styles.reviewTitle}>Review your agent</h3>
              <div className={styles.reviewGrid}>
                <div className={styles.reviewItem}><span className={styles.reviewKey}>Name</span><span>{form.name}</span></div>
                <div className={styles.reviewItem}><span className={styles.reviewKey}>Description</span><span>{form.description || '—'}</span></div>
                <div className={styles.reviewItem}><span className={styles.reviewKey}>Channels</span><span>{form.routingChannels.length > 0 ? form.routingChannels.join(', ') : 'All channels'}</span></div>
                <div className={styles.reviewItem}><span className={styles.reviewKey}>Priority</span><span>{form.routingPriority}</span></div>
                <div className={styles.reviewItem}><span className={styles.reviewKey}>Max turns</span><span>{form.maxTurnsBeforeEscalation}</span></div>
                <div className={styles.reviewItem}>
                  <span className={styles.reviewKey}>System prompt</span>
                  <span className={styles.promptPreview}>{form.systemPrompt || 'Default'}</span>
                </div>
              </div>
              {error && <div className={styles.errorBanner}>{error}</div>}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className={styles.footer}>
          {step > 0 && (
            <button className={styles.backBtn} onClick={() => setStep(s => s - 1)} disabled={saving}>
              <ChevronLeft size={16} /> Back
            </button>
          )}
          <div style={{ flex: 1 }} />
          {step < STEPS.length - 1 ? (
            <button
              id="next-step"
              className={styles.nextBtn}
              onClick={() => setStep(s => s + 1)}
              disabled={!canNext}
            >
              Next <ChevronRight size={16} />
            </button>
          ) : (
            <button
              id="create-agent"
              className={styles.createBtn}
              onClick={handleCreate}
              disabled={saving}
            >
              {saving ? 'Creating…' : <><Check size={16} /> Create Agent</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
